import HydrationWrapper from "@/components/hydration-wrapper";
import "../styles/globals.scss";
import { CeeqLayout, useGetLayout } from "@/components/layout";
import Loading from "@/components/loading";
import QueryClientProvider from "@/providers/query-client.provider";
import { ConfigProvider } from "antd";
import jaJP from "antd/es/locale/ja_JP";
import type { NextPage } from "next";
import type { AppProps } from "next/app";
import Head from "next/head";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export type NextPageWithLayout = NextPage & {
  layout?: CeeqLayout;
};

export type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

// タイトルを自前で設定していない画面は、next/head の性質上「直前に見た画面の
// タイトル」がそのまま残る (/task を開いているのにタブが ceeq.io/setting/integrations の
// まま、など)。ここで既定値を先に描画しておけば、ページ側が <title> を出す場合は
// そちらが勝ち、出さない場合でも現在地に合ったタイトルになる。
const ROUTE_TITLES: Record<string, string> = {
  "/task": "タスク",
  "/calendar": "カレンダー",
  "/calendar/create": "予約枠作成",
  "/profile": "プロフィール",
  "/notifications": "通知",
  "/messages/search": "メッセージ検索",
  "/contract": "契約",
  "/payment": "お支払い",
  "/setting": "設定",
  "/setting/accounts": "アカウント管理",
  "/setting/properties": "カスタムプロパティ",
  "/setting/business-days": "営業日設定",
  "/setting/calendar-connect": "カレンダー連携",
  "/setting/integrations": "外部サービス連携",
  "/setting/link-sns": "外部ツール連携",
  "/setting/payment-permissions": "支払い権限",
  "/setting/plan": "契約プラン",
};

function useDefaultTitle() {
  const { pathname } = useRouter();
  return ROUTE_TITLES[pathname] ? `${ROUTE_TITLES[pathname]} | Ceeq` : "Ceeq";
}

function CeeqApp({ Component, pageProps }: AppPropsWithLayout) {
  const Layout = useGetLayout(Component.layout);
  const router = useRouter();
  const defaultTitle = useDefaultTitle();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const handleRouteChangeStart = () => {
      if (timeoutId) clearTimeout(timeoutId);
      setIsLoading(true);
    };

    const handleRouteChangeComplete = () => {
      requestAnimationFrame(() => {
        timeoutId = setTimeout(() => {
          setIsLoading(false);
        }, 50);
      });
    };

    const handleRouteChangeError = () => {
      if (timeoutId) clearTimeout(timeoutId);
      setIsLoading(false);
    };

    router.events.on("routeChangeStart", handleRouteChangeStart);
    router.events.on("routeChangeComplete", handleRouteChangeComplete);
    router.events.on("routeChangeError", handleRouteChangeError);

    // Đảm bảo tắt loading khi component mount
    setIsLoading(false);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      router.events.off("routeChangeStart", handleRouteChangeStart);
      router.events.off("routeChangeComplete", handleRouteChangeComplete);
      router.events.off("routeChangeError", handleRouteChangeError);
    };
  }, [router]);

  return (
    // antd の日本語ロケールをアプリ全体に適用する。以前は components/calendar と
    // components/datepicker だけが個別に ConfigProvider を持っていたため、
    // それ以外の 28 ファイルの Calendar / DatePicker が英語のまま表示されていた
    // (タスクの期日ポップアップが Su/Mo/Tu・Aug・Month/Year になる)。
    // Select の "No data" やページネーションなど antd 組み込みの文言も同時に日本語になる。
    <ConfigProvider locale={jaJP}>
      <Head>
        <title>{defaultTitle}</title>
      </Head>
      <QueryClientProvider>
        <HydrationWrapper>
          <Layout>
            {isLoading ? <Loading /> : <Component {...pageProps} />}
            <ToastContainer position="top-right" autoClose={5000} />
          </Layout>
        </HydrationWrapper>
      </QueryClientProvider>
    </ConfigProvider>
  );
}

export default CeeqApp;
